/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

class SampleProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Get the Sample Status Mapped Details - this provides a consistent breakdown of the status of a sample for display purposes
	 * @param {string} pStatusString - The status string to map
	 * @return {Object} - The mapped details of the status
	 */
	getSampleStatusMappedDetails(pStatusString)
	{
		switch (pStatusString ? pStatusString.toLowerCase() : '')
		{
		case 'pass':
		case 'sqty':
		case 'passing':
			return {
				name: 'Pass',
				color: 'is-success',
				icon: 'fa-check-circle',
				iconColor: 'has-text-success-soft',
				original: pStatusString,
			};
		case 'fail':
		case 'failing':
			return {
				name: 'Fail',
				color: 'is-danger',
				icon: 'fa-exclamation-circle',
				iconColor: 'has-text-danger-soft',
				original: pStatusString,
			};
		case 'pending':
			return {
				name: 'Pending',
				color: 'is-warning',
				icon: 'fa-clock',
				iconColor: 'has-text-warning-soft',
				original: pStatusString,
			};
		case 'void':
			return {
				name: 'Void',
				color: 'is-text',
				icon: 'fa-ban',
				iconColor: 'has-text-text-60',
				original: pStatusString,
			};
		case 'info':
			return {
				name: 'Info',
				color: 'is-info',
				icon: 'fa-info-circle',
				iconColor: 'has-text-info-soft',
				original: pStatusString,
			};
		default:
			if (pStatusString)
			{
				return {
					name: pStatusString,
					color: 'is-unknown',
					icon: 'fa-info-circle',
					iconColor: 'has-text-unkown',
					original: pStatusString,
				};
			}
			return {
				name: 'N/A',
				color: 'is-text',
				icon: 'fa-question-circle',
				iconColor: 'has-text-text-60',
				original: pStatusString,
			};
		}
	}

	/**
	 * Get the Sample by ID
	 * @param {String|Number} pSampleID - The Sample ID to search for
	 * @return {Promise<void>}
	 * @async
	 */
	getSampleByID(pSampleID)
	{
		return new Promise((resolve, reject) =>
		{
			if (!pSampleID)
			{
				this.pict.log.error('No Sample ID provided.');
				return reject(new Error('No Sample ID provided.'));
			}

			this.pict.HeadlightRestClient.getJSON(`Sample/${pSampleID}`, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting Sample: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Get the Sample by GUID
	 * @param {String|Number} pSampleGUID - The Sample GUID to search for
	 * @return {Promise<void>}
	 * @async
	 */
	getSampleByGUID(pSampleGUID)
	{
		return new Promise((resolve, reject) =>
		{
			if (!pSampleGUID)
			{
				this.pict.log.error('No Sample GUID provided.');
				return reject(new Error('No Sample GUID provided.'));
			}

			this.pict.HeadlightRestClient.getJSON(`Samples/FilteredTo/FBV~GUIDSample~EQ~${pSampleGUID}`, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting Sample: ${pError}`, pError);
					return reject(pError);
				}
				if (pBody.length > 1)
				{
					this.pict.log.error(`More than one Sample found for GUID: ${pSampleGUID}`, pBody);
				}
				resolve(pBody[0]);
			});
		});
	}

	/**
	 * Get the Sample by QR Code
	 * @param {Object} pQRCode - The QR Code to search for
	 * @return {Promise<void>}
	 * @async
	 */
	getSampleByQRCode(pQRCode)
	{
		return new Promise((resolve, reject) =>
		{
			if (!pQRCode)
			{
				this.pict.log.error('No QR Code provided.');
				return reject(new Error('No QR Code provided.'));
			}

			this.pict.HeadlightRestClient.getJSON(`Samples/FilteredTo/FBV~QRCode~EQ~${pQRCode}`, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting Sample: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Get the Samples
	 * @return {Promise<void>}
	 * @async
	 * @example
	 */
	getSamples()
	{
		return new Promise((resolve, reject) =>
		{
			let tmpURL = 'Samples';

			// Filter by customer if IDCustomer is present
			if (this.pict.AppData?.DocumentData?.ReportData?.IDCustomer)
			{
				tmpURL = `Samples/By/IDCustomer/${this.pict.AppData?.DocumentData?.ReportData?.IDCustomer}`;
			}

			this.pict.HeadlightRestClient.getJSON(tmpURL, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error('Failed to get Samples list.', pError);
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Promise wrapper over the paged entity-set fetch.
	 * @param {String} pEntity - The entity type to fetch
	 * @param {String} pFilter - A Meadow filter string
	 * @return {Promise<Array<Object>>}
	 */
	_getEntitySet(pEntity, pFilter)
	{
		// the paged client takes per-page / progress callbacks we have no use for here
		const fNoOp = () =>
		{
			// intentionally empty
		};
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getEntitySetWithPages(pEntity, pFilter, fNoOp, fNoOp, (pError, pRecords) =>
			{
				if (pError)
				{
					this.pict.log.error(`Failed to get the ${pEntity} set.`, pError);
					return reject(pError);
				}
				resolve(pRecords || []);
			});
		});
	}

	/**
	 * Get the DocumentPolyJoin rows tying Samples to a Document. The joins are written server-side
	 * (SampleTestController), so a freshly created Sample only appears here after a save round-trip.
	 * @param {Number|String} pIDDocument - The Document to fetch Sample joins for
	 * @return {Promise<Array<Object>>}
	 * @async
	 */
	async getDocumentSampleJoins(pIDDocument)
	{
		if (!pIDDocument)
		{
			return [];
		}
		return await this._getEntitySet('DocumentPolyJoin', `FBV~RemoteType~EQ~Sample~FBV~IDDocument~EQ~${pIDDocument}`);
	}

	/**
	 * Get the Samples joined to a Document. Replaces the legacy sample-log component's raw
	 * `Samples/FilteredTo/...?Joins=DocumentPolyJoin` URL with the standard two-hop entity fetch.
	 * @param {Number|String} pIDDocument - The Document to fetch joined Samples for
	 * @return {Promise<Array<Object>>}
	 * @async
	 */
	async getDocumentJoinedSamples(pIDDocument)
	{
		const tmpJoins = await this.getDocumentSampleJoins(pIDDocument);
		const tmpSampleIDs = [ ...new Set(tmpJoins.map((pJoin) => pJoin.IDRemote).filter((pIDRemote) => !!pIDRemote)) ];
		if (!tmpSampleIDs.length)
		{
			return [];
		}
		return await this._getEntitySet('Sample', `FBL~IDSample~INN~${tmpSampleIDs.join(',')}`);
	}

	/**
	 * Get the Sample search count
	 * @param {Object} pSearchQuery - The search query object to use when performing the search
	 * @return {Promise<void>}
	 * @async
	 * @example
	 * getObservationSearchCount({ IDProject: 1234 }, (count) => { console.log(count); });
	 */
	async getSampleSearchCount(pSearchQuery)
	{
		const tmpFilter = this.pict.providers.UtilityProvider.generateMeadowFilter(pSearchQuery);
		const tmpURL = `Samples/Count${tmpFilter ? `/${tmpFilter}` : ''}`;

		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(tmpURL, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error performing request for Count: ${pError}`, pError);
					reject(pError);
				}
				else
				{
					resolve(pBody?.Count || 0);
				}
			});
		});
	}

	/**
	 * Get the Sample search results
	 * @param {Object} pSearchQuery - The search query object to use when performing the search
	 * @return {Promise<Array>}
	 * @async
	 * @example
	 * getSampleSearchResults({ IDProject: 1234 }, (results) => { console.log(results); });
	 */
	async getSampleSearchResults(pSearchQuery)
	{
		const tmpURL = `Samples/` + this.pict.providers.UtilityProvider.generateMeadowFilter(pSearchQuery);
		const startCount = (pSearchQuery.pagination.start - 1) * pSearchQuery.pagination.limit;
		const endCount = pSearchQuery.pagination.start * pSearchQuery.pagination.limit;
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(`${tmpURL}/${startCount}/${endCount}`, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error performing search: ${pError}`, pError);
					reject(pError);
				}
				resolve(Array.isArray(pBody) ? pBody : []);
			});
		});
	}

	/**
	 * This method performs an Sample search based on the current search filters.
	 * @param {Object} pOptions - The options to use when performing the search.
	 * @return {Promise<Object>}
	 * @async
	 */
	performSampleSearch(pOptions = {})
	{
		return new Promise((resolve, reject) =>
		{
			if (!pOptions)
			{
				this.pict.log.error('No search options provided.');
				return reject(new Error('No search options provided.'));
			}

			const tmpSearchQuery = {
				filters: [ {
					field: 'IDCustomer',
					comparator: 'EQ',
					target: pOptions.IDCustomer || 0,
				} ],
				pagination: {
					start: pOptions?.CurrentPage || 1,
					limit: pOptions?.PageSize || 25,
				},
				sort: {
					field: pOptions.SortBy || 'CreateDate',
					direction: pOptions.SortDirection || 'DESC',
				},
			};

			// Handle the search filters
			if (pOptions?.Projects && pOptions.Projects.length > 0)
			{
				tmpSearchQuery.filters.push({
					field: 'IDProject',
					comparator: pOptions.Projects.length === 1 ? 'EQ' : 'INN',
					target: pOptions.Projects.length === 1 ? pOptions.Projects[0] : pOptions.Projects,
				});
			}

			if (pOptions?.Materials && pOptions.Materials.length > 0)
			{
				tmpSearchQuery.filters.push({
					field: 'IDMaterial',
					comparator: pOptions.Materials.length === 1 ? 'EQ' : 'INN',
					target: pOptions.Materials.length === 1 ? pOptions.Materials[0] : pOptions.Materials,
				});
			}

			if (pOptions?.MixDesigns && pOptions.MixDesigns.length > 0)
			{
				tmpSearchQuery.filters.push({
					field: 'IDMixDesign',
					comparator: pOptions.MixDesigns.length === 1 ? 'EQ' : 'INN',
					target: pOptions.MixDesigns.length === 1 ? pOptions.MixDesigns[0] : pOptions.MixDesigns,
				});
			}

			if (pOptions?.StartDate && pOptions.StartDate.length > 0)
			{
				tmpSearchQuery.filters.push({
					field: 'CreateDate',
					comparator: 'GE',
					target: pOptions.StartDate,
				});
			}

			if (pOptions?.EndDate && pOptions.EndDate.length > 0)
			{
				tmpSearchQuery.filters.push({
					field: 'CreateDate',
					comparator: 'LE',
					target: pOptions.EndDate,
				});
			}

			// Perform the search by calling both getSampleSearchCount and getSampleSearchResults
			Promise.all([
				this.getSampleSearchCount(tmpSearchQuery),
				this.getSampleSearchResults(tmpSearchQuery),
			])
				.then(([ tmpCountResults, tmpResults ]) =>
				{
					resolve({ results: tmpResults, count: tmpCountResults });
				})
				.catch((error) =>
				{
					this.pict.log.error('Error during sample search:', error);
					reject(error);
				});
		});
	}

	/**
	 * Create a new Sample
	 * @param {Object} pSampleData - The Sample data to create
	 * @param {Function} [pCallback] - The callback function to call when the creation is complete
	 * @param {Boolean} [disableAutomaticDesignatedTime] - If true, the Sample will not get a date automatically set.
	 *
	 * @return {Promise<void>}
	 * @async
	 * FIXME: Why do we have an async method with a callback that is only invoked sometimes?
	 */
	async createSample(pSampleData, pCallback, disableAutomaticDesignatedTime)
	{
		if (!pSampleData)
		{
			this.pict.log.error('No Sample data provided.');
			throw new Error('No Sample data provided.');
		}
		// get the customer record to use for the QR code hash generation (we need the legacy format check)
		const useLegacyFormat = await new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON('Customer/' + this.pict.AppData?.CurrentUser?.CustomerID, (pError, pResult) =>
			{
				if (pError)
				{
					this.pict.log.error('Error getting customer record:', pError);
					return reject(pError);
				}
				let tmpCustomerProperties = pResult?.CustomProperties || {};
				// check if we need to parse the custom properties
				if (typeof tmpCustomerProperties === 'string' && tmpCustomerProperties.trim().startsWith('{'))
				{
					tmpCustomerProperties = JSON.parse(tmpCustomerProperties);
				}
				// check if we need to use the legacy format
				const tmpLegacyFormat = pResult && tmpCustomerProperties?.CustomSampleGUID === 'SiteManagerSampleGUID';
				resolve(tmpLegacyFormat);
			});
		});
		// if we are using legacy format, we also need to get the user record to use for the QR code hash generation
		if (useLegacyFormat === true)
		{
			this.pict.log.info('Using Legacy Sample GUID format.');
			// get the user record to use for the QR code hash generation
			const tmpUserRecord = await new Promise((resolve, reject) =>
			{
				this.pict.HeadlightRestClient.getJSON('User/' + this.pict.AppData?.CurrentUser?.UserID, (pError, pResult) =>
				{
					if (pError)
					{
						this.pict.log.error('Error getting user record:', pError);
						return reject(pError);
					}
					resolve(pResult);
				});
			});
			// LADOTD QRs feed SiteManager, which keys on the user's external (SiteManager) id. With no
			// ExternalSyncGUID, mint a deterministic placeholder "HL<IDUser>" — matching the materials app
			// (sample.ts generateGUID) — so the format stays valid instead of degrading to a plain user GUID.
			pSampleData.QRCode = this.pict.fable.services.HeadlightQRCodeHash.generateQRCodeHash({
				GUIDPrefix: pSampleData.GUIDPrefix || tmpUserRecord?.ExternalSyncGUID || `HL${this.pict.AppData?.CurrentUser?.UserID}`,
				LegacyFormat: true,
			});
		}
		else
		{
			this.pict.log.info('Using Standard Sample GUID format.');
			pSampleData.QRCode = this.pict.fable.services.HeadlightQRCodeHash.generateQRCodeHash({ IDProject: pSampleData.IDProject, IDUser: this.pict.AppData?.CurrentUser?.UserID });
		}
		if (pSampleData.DesignatedTime?.length && !pSampleData.DesignatedTime.includes('T'))
		{
			pSampleData.DesignatedTime = this.fable.Dates.dayJS(pSampleData.DesignatedTime + this.getCurrentTime()).toISOString();
		}
		// Set the Designated Time to the current time if not provided (unless explcitly disabled)
		if (!pSampleData.DesignatedTime && !disableAutomaticDesignatedTime)
		{
			this.pict.log.info('No Designated Time was passed, setting to current time.');
			pSampleData.DesignatedTime = new Date().toISOString();
		}
		// strip out any _ prefixed fields (these are internal only)
		Object.keys(pSampleData).forEach((pKey) =>
		{
			if (pKey.startsWith('_'))
			{
				delete pSampleData[pKey];
			}
		});
		// Go build us a sample record
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.postJSON('Sample', this.buildSampleAPIPayload(pSampleData), async (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error creating Sample: ${pError}`, pError);
					return reject(pError);
				}
				if (parseInt(pBody?.Extension?.StorageLocation))
				{
					await this.pict.providers.LocationProvider.addSampleToLocation(pBody.IDSample, parseInt(pBody?.Extension?.StorageLocation));
				}
				this.pict.log.info('Sample created successfully.');
				// go create a sample log record for the creation step
				this.pict.providers.SampleLogProvider.createSampleLog({
					IDSample: pBody.IDSample,
					IDUser: pBody.CreatingIDUser,
					IDSampleStatus: pBody.IDSampleStatus,
					Notes: 'Sample created.',
				}).catch((pError) =>
				{
					this.pict.log.error(`Error creating Sample Log for newly created sample: ${pError}`, pError);
				}).finally(() =>
				{
					resolve(pBody);
					if (pCallback)
					{
						pCallback(pBody);
					}
				});
			});
		});
	}

	getCurrentTime()
	{
		return ` ${ this.fable.Dates.dayJS(new Date()).format('HH:mm:ss') }`;
	}

	/**
	 * The Sample record as the API should see it.
	 *
	 * Entity pickers keep a `<field>__Record` snapshot beside each value so the control can paint its
	 * selection without a refetch. That is client-side display state — the Sample schema has no such
	 * column — so it is dropped here rather than posted. A COPY, deliberately: the caller's object is the
	 * live form's data, and the picker reads those snapshots, so removing them in place would blank the
	 * control after a save.
	 *
	 * @param {Object} pSampleData - The Sample data being sent.
	 * @return {Object} a shallow copy without the `__Record` snapshots.
	 */
	buildSampleAPIPayload(pSampleData)
	{
		const tmpPayload = {};
		for (const tmpKey of Object.keys(pSampleData))
		{
			if (tmpKey.endsWith('__Record'))
			{
				continue;
			}
			tmpPayload[tmpKey] = pSampleData[tmpKey];
		}
		return tmpPayload;
	}

	/**
	 * Update an existing Sample
	 * @param {Object} pSampleData - The Sample data to update
	 * @return {Promise<void>}
	 * @async
	 */
	async updateSample(pSampleData)
	{
		if (!pSampleData)
		{
			this.pict.log.error('No Sample data provided.');
			throw new Error('No Sample data provided.');
		}
		if (pSampleData.DesignatedTime?.length && !pSampleData.DesignatedTime.includes('T'))
		{
			pSampleData.DesignatedTime = this.fable.Dates.dayJS(pSampleData.DesignatedTime + this.getCurrentTime()).toISOString();
		}
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.putJSON('Sample/Upsert', this.buildSampleAPIPayload(pSampleData), async (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error updating Sample: ${pError}`, pError);
					return reject(pError);
				}
				if (parseInt(pBody?.Extension?.StorageLocation))
				{
					await this.pict.providers.LocationProvider.addSampleToLocation(pBody.IDSample, parseInt(pBody?.Extension?.StorageLocation));
				}

				resolve(pBody);
			});
		});
	}

	/**
	 * Delete an existing Sample
	 * @param {Object} pSampleId - The IDSample to delete the Sample record
	 * @return {Promise<void>}
	 * @async
	 */
	async deleteSample(pSampleId)
	{
		if (!pSampleId)
		{
			this.pict.log.error('No Sample id provided.');
			throw new Error('No Sample id provided.');
		}
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.deleteEntity('Sample', pSampleId, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error deleting Sample: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Get the Sample schema
	 * @return {Promise<void>}
	 * @async
	 */
	async getSampleSchema()
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON('Sample/Schema', (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting Sample schema: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}
}

module.exports = SampleProvider;
module.exports.default_configuration = {};
